/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, computed, OnInit, signal} from '@angular/core';
import {EsciComponent} from '../esci.component';
import {RoleModificationOptions} from '../../model/roleModificationOptions.model';

/**
 * IT Admin Tools -> Change Roles.
 *
 * Modern eSCI-tools port of the legacy roleModification.jsp: lets an eSCI IT Admin
 * temporarily take on a chosen set of roles for a timeout window, to see the app as
 * another user would. Self-service and ephemeral - it only ever affects the logged-in
 * user's own roles and nothing is persisted. Backed by the /esciUI/roleModification*
 * endpoints, which delegate to the same in-memory TemporaryRoleService as eSCI Classic.
 */
@Component({
    selector: 'app-change-roles',
    templateUrl: './change-roles.component.html',
    styleUrls: ['./change-roles.component.scss'],
    standalone: false
})
export class ChangeRolesComponent extends EsciComponent implements OnInit {
    readonly MIN_TIMEOUT = 1;
    readonly MAX_TIMEOUT = 1440;
    readonly DEFAULT_TIMEOUT = 10;

    readonly options = signal<RoleModificationOptions | null>(null);
    readonly selected = signal<Set<string>>(new Set<string>());
    readonly timeoutMinutes = signal<number>(this.DEFAULT_TIMEOUT);
    readonly loading = signal<boolean>(true);
    readonly saving = signal<boolean>(false);
    /** True when the options request was rejected (403) - caller lacks the required admin roles. */
    readonly accessDenied = signal<boolean>(false);

    /** The page is interactive only when the feature is enabled and the user is an IT admin. */
    readonly canUse = computed(() => {
        const o = this.options();
        return !!o && o.enabled && o.canModify;
    });

    /** Clear is allowed without IT Admin so users can recover after dropping ESCI_IT_ADMIN via override. */
    readonly canClearTemporaryRoles = computed(() => this.canUse() || this.accessDenied());

    /** True when the current selection differs from the roles applied right now. */
    readonly dirty = computed(() => {
        const o = this.options();
        if (!o) {
            return false;
        }
        const current = new Set(o.currentRoles);
        const sel = this.selected();
        if (current.size !== sel.size) {
            return true;
        }
        for (const role of sel) {
            if (!current.has(role)) {
                return true;
            }
        }
        return false;
    });

    ngOnInit(): void {
        // Access is enforced by roleModificationOptions (403 -> accessDenied). Do not gate the page
        // on esciITAdminPermission or the recovery UI never renders for locked-out users.
        this.permission.set(true);
        this.loadOptions();
    }

    loadOptions(): void {
        this.loading.set(true);
        this.accessDenied.set(false);
        this.esciService.getRoleModificationOptions().subscribe({
            next: (options) => {
                this.options.set(options);
                this.selected.set(new Set(options.currentRoles));
                this.timeoutMinutes.set(this.DEFAULT_TIMEOUT);
                this.loading.set(false);
                this.saving.set(false);
            },
            error: (err) => {
                this.loading.set(false);
                this.saving.set(false);
                if (err?.status === 403) {
                    // Not an ESCI_IT_ADMIN - show the access-denied panel, not an error.
                    this.accessDenied.set(true);
                } else {
                    this.messageService.processError('Unable to load role options.');
                }
            }
        });
    }

    isSelected(role: string): boolean {
        return this.selected().has(role);
    }

    /** True when the role is one of the user's real roles from Graph. */
    isReal(role: string): boolean {
        return this.options()?.realRoles.includes(role) ?? false;
    }

    countSelected(roles: string[]): number {
        const sel = this.selected();
        return roles.filter((role) => sel.has(role)).length;
    }

    toggle(role: string): void {
        if (!this.canUse()) {
            return;
        }
        const next = new Set(this.selected());
        if (next.has(role)) {
            next.delete(role);
        } else {
            next.add(role);
        }
        this.selected.set(next);
    }

    onTimeoutChange(value: number): void {
        const parsed = Math.floor(Number(value));
        const safe = Number.isFinite(parsed) ? parsed : this.DEFAULT_TIMEOUT;
        this.timeoutMinutes.set(Math.max(this.MIN_TIMEOUT, Math.min(this.MAX_TIMEOUT, safe)));
    }

    step(delta: number): void {
        this.onTimeoutChange(this.timeoutMinutes() + delta);
    }

    save(): void {
        if (!this.canUse()) {
            return;
        }
        this.saving.set(true);
        this.esciService.setTemporaryRoles([...this.selected()], this.timeoutMinutes()).subscribe({
            next: () => {
                this.messageService.processSuccess('Temporary roles saved.');
                this.loadOptions();
            },
            error: (err) => {
                this.saving.set(false);
                this.messageService.processError('Failed to save roles. ' + (err?.error ?? ''));
            }
        });
    }

    clearRoles(): void {
        if (!this.canClearTemporaryRoles()) {
            return;
        }
        this.saving.set(true);
        this.esciService.clearTemporaryRoles().subscribe({
            next: () => {
                this.messageService.processSuccess('Temporary roles cleared.');
                this.loadOptions();
            },
            error: (err) => {
                this.saving.set(false);
                this.messageService.processError('Failed to clear roles. ' + (err?.error ?? ''));
            }
        });
    }

    refresh(): void {
        this.loadOptions();
    }
}
